/**
 * Math Exercise Engine - Natural DSL
 *
 * A natural DSL parser and validator for math exercises.
 * Write exercises naturally: `25 + W_S_I[name="x", length=2, answer="5"] = 30`
 *
 * @packageDocumentation
 */

// ═══════════════════════════════════════════════════════════════════════════
// Type Constants
// ═══════════════════════════════════════════════════════════════════════════

export { AST_TYPES, JSON_TYPES, type ASTType, type JSONType } from './constants';
export { getFractionKey, isFractionKey, parseFractionKey, type FractionPart } from './utils/fraction-keys';

// ═══════════════════════════════════════════════════════════════════════════
// Parsing Pipeline
// ═══════════════════════════════════════════════════════════════════════════

export { tokenize, tokenizeSafe } from './lexer';
export { parse, parseSafe, toAST } from './parser';
export { toJSON } from './transformers';

// ═══════════════════════════════════════════════════════════════════════════
// Validation
// ═══════════════════════════════════════════════════════════════════════════

export {
  validate,
  getMissingInputs,
  getAnswerableInputs,
  validateInputFormats,
  validateSimple,
  validateExpression,
  validateMixed,
  findValidCombination,
  evaluate,
  isValidExpression,
  extractVariables as extractExpressionVariables,
  validateBooleanExpression
} from './validators';


// ═══════════════════════════════════════════════════════════════════════════
// Types - AST (Natural DSL)
// ═══════════════════════════════════════════════════════════════════════════

export type {
  // Document & Blocks
  ExerciseDocument,
  ContentBlock,
  TextBlock,
  LineBlock,

  // Table
  TableBlock,
  TableRow,
  TableCell,
  // Number Line
  NumberLineBlock,
  NumberLinePosition,
  NumberLineCounter,
  NumberLineHighlight,
  NumberLineCursor,
  NumberLineArrow,
  // Inline Elements
  FreeTextInline,
  HighlightedTextInline,
  ColorTextInline,
  BoldTextInline,
  InlineInputWidget,
  FractionInline,
  ImageInline,
  ClockInline,
  InlineNode,

  // Radio Options
  RadioOptionsBlock,

  // Tools
  DragOptionsTool,
  NumericKeypadTool,
  ToolNode,

  // Validation
  SimpleValidation,
  ExpressionValidation,
  MixedValidation,
  ValidationNode
} from './ast';

// Re-export AST modules
export * from './ast/inline';
export * from './ast/blocks';
export * from './ast/matcher-item';
export * from './ast/matcher';
export * from './ast/pair-matcher';
export * from './ast/tools';
export * from './ast/validation';
export * from './ast/document';
export * from './ast/vertical-operation';

// ═══════════════════════════════════════════════════════════════════════════
// Types - JSON Schema
// ═══════════════════════════════════════════════════════════════════════════

export type {
  // Main Types
  ExerciseJSON,
  ExerciseMeta,
  ValidationJSON,

  // Layout Types
  LayoutItem,
  QuestionText,
  TextItem,
  WidgetRef,
  HighlightedText,
  ColorText,
  BoldText,
  SpaceItem,
  Linebreak,
  GroupItem,
  OperatorItem,
  TableItem,
  TableRowItem,
  TableCellItem,
  ImageItem,
  NumberLineItem,
  NumberLinePositionItem,
  NumberLineCounterItem,
  NumberLineHighlightItem,
  NumberLineCursorItem,
  NumberLineArrowItem,
  VopCellItem,
  VopRowItem,
  VopCarryRowItem,
  VopSeparatorItem,
  VerticalOperationItem,
  VopDigitCellItem,
  VopLabelCellItem,
  VopInputCellItem,
  VopEmptyCellItem,
  VopColorCellItem,
  VopBoldCellItem,
  VopHighlightCellItem,
  ClockItem,
  // Widget Types
  WidgetJSON,
  InlineInputJSON,
  DragOptionsJSON,
  RadioOptionsJSON,
  NumericKeypadJSON,
  FractionJSON,
  MatcherWidgetJSON,
  MatcherRowItem,
  MatcherItemContent,
  PairMatcherWidgetJSON,
  RulerWidgetJSON,
  ProtractorWidgetJSON,
  SetSquareWidgetJSON,
} from './transformers';

// Re-export type guards
export {
  isQuestionText,
  isTextItem,
  isWidgetRef,
  isHighlightedText,
  isColorText,
  isBoldText,
  isSpaceItem,
  isLinebreak,
  isGroupItem,
  isOperatorItem,
  isInlineInputJSON,
  isDragOptionsJSON,
  isInlineDragOptionJSON,
  isRadioOptionsJSON,
  isNumericKeypadJSON,
  isFractionJSON,
  isTableItem,
  isTableRowItem,
  isTableCellItem,
  isImageItem,
  isVerticalOperationItem,
  isClockItem,
  isRulerWidget,
  isProtractorWidget,
  isSetSquareWidget,
} from './transformers';

// ═══════════════════════════════════════════════════════════════════════════
// Runtime Validation - Zod Schemas
// ═══════════════════════════════════════════════════════════════════════════

export {
  // Main Schemas
  ExerciseJSONSchema,
  ExerciseMetaSchema,
  ValidationJSONSchema,

  // Layout Schemas
  LayoutItemSchema,
  QuestionTextSchema,
  TextItemSchema,
  WidgetRefSchema,
  HighlightedTextSchema,
  ColorTextSchema,
  BoldTextSchema,
  ImageItemSchema,
  SpaceItemSchema,
  LinebreakSchema,
  GroupItemSchema,
  OperatorItemSchema,
  OperatorSchema,
  TableItemSchema,
  TableRowItemSchema,
  TableCellItemSchema,
  NumberLineItemSchema,
  NumberLinePositionItemSchema,
  NumberLineCounterItemSchema,
  NumberLineHighlightItemSchema,
  NumberLineCursorItemSchema,
  NumberLineArrowItemSchema,
  VerticalOperationItemSchema,
  VopRowItemSchema,
  VopCarryRowItemSchema,
  VopSeparatorItemSchema,
  VopCellItemSchema,
  VopColorCellItemSchema,
  VopBoldCellItemSchema,
  VopHighlightCellItemSchema,
  ClockItemSchema,

  // Widget Schemas
  WidgetJSONSchema,
  InlineInputJSONSchema,
  DragOptionsJSONSchema,
  RadioOptionsJSONSchema,
  NumericKeypadJSONSchema,
  FractionJSONSchema,
  MatcherWidgetJSONSchema,
  PairMatcherWidgetJSONSchema,
  RulerWidgetSchema,
  ProtractorWidgetSchema,
  SetSquareWidgetSchema,

  // Validation helpers
  validateExerciseJSON,
  validateExerciseJSONSafe,
  validateWidgetJSON,
  validateWidgetJSONSafe
} from './transformers';

// ═══════════════════════════════════════════════════════════════════════════
// Types - Validation
// ═══════════════════════════════════════════════════════════════════════════

export type {
  ValidationResult,
  WidgetValidation
} from './validators';

// ═══════════════════════════════════════════════════════════════════════════
// Types - Errors
// ═══════════════════════════════════════════════════════════════════════════

export {
  EngineError,
  LexerError,
  ParseError,
  TransformError,
  ValidationError
} from './errors';

export type { SourcePosition } from './errors';

// ═══════════════════════════════════════════════════════════════════════════
// Convenience Functions
// ═══════════════════════════════════════════════════════════════════════════

import { tokenize } from './lexer';
import { parse, toAST } from './parser';
import { toJSON } from './transformers';
import { ExerciseJSON } from './transformers';

/**
 * Compile DSL text directly to JSON (convenience function)
 *
 * This combines tokenize → parse → toAST → toJSON in one call.
 *
 * @param dsl - The DSL text to compile
 * @returns The exercise JSON
 * @throws LexerError if tokenization fails
 * @throws ParseError if parsing fails
 *
 * @example
 * ```typescript
 * const json = compile(`
 *   QUESTION_TEXT["Solve"]
 *   25 + W_S_I[name="x", length=2, answer="5"] = 30
 *   WIDGET_ANSWER[type="simple"]
 * `);
 * ```
 */
export function compile(dsl: string): ExerciseJSON {
  const tokens = tokenize(dsl);
  const cst = parse(tokens);
  const ast = toAST(cst);
  return toJSON(ast);
}

/**
 * Compile DSL and return both AST and JSON
 *
 * Useful when you need access to both representations.
 *
 * @param dsl - The DSL text to compile
 * @returns Object containing both ast and json
 */
export function compileWithAST(dsl: string) {
  const tokens = tokenize(dsl);
  const cst = parse(tokens);
  const ast = toAST(cst);
  const json = toJSON(ast);
  return { ast, json };
}

// ═══════════════════════════════════════════════════════════════════════════
// Renderer (requires React)
// ═══════════════════════════════════════════════════════════════════════════

export {
  ExerciseRenderer,
  LayoutRenderer,
  WidgetRenderer,
  renderExercise,
  SoundProvider,
  useSound
} from './renderer';

export {
  isPairMatcherWidget,
  isMatcherWidget,
} from './renderer/widgets/type-guards';

export type {
  ExerciseRendererProps,
  ExerciseRendererRef,
  InputCompletion,
  LayoutRendererProps,
  WidgetRendererProps,
  RenderOptions,
  SoundEvent,
  SoundConfig,
  SoundProviderProps
} from './renderer';
